import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Grid, Paper, Typography, Button, CircularProgress, Alert, Stack, Chip,
  List, ListItemButton, ListItemText,
} from '@mui/material';
import {
  Print, PictureAsPdf, GridOn, Refresh, AccountBalance,
} from '@mui/icons-material';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { bankAccountsURL, reportBankStatementURL } from '../../BaseURL';

function headers() { return { Authorization: `Bearer ${localStorage.getItem('church_token')}` }; }

const fmt = (n) =>
  Number(n || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtSigned = (n) => {
  const v = Number(n || 0);
  const s = v.toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `(${s.replace('-', '')})` : s;
};

const fmtDateLong = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-GH', { day: '2-digit', month: 'long', year: 'numeric' });
};

const fmtDateShort = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-GH', { day: '2-digit', month: 'short', year: 'numeric' });
};

const NAVY  = '#0B2545';
const GREEN = '#0F766E';
const RED   = '#B91C1C';
const GOLD  = '#C89211';
const RULE  = '#0B2545';

export default function BankStatement({ start, end }) {
  const [accounts, setAccounts]   = useState([]);
  const [accountsErr, setAcErr]   = useState('');
  const [accountsLoading, setAcL] = useState(true);
  const [selectedId, setSelected] = useState(null);
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const printRef = useRef(null);

  // Load bank accounts list once
  useEffect(() => {
    setAcL(true); setAcErr('');
    axios.get(bankAccountsURL, { headers: headers() })
      .then(r => {
        if (r.data.success) {
          setAccounts(r.data.data || []);
          if (!selectedId && r.data.data && r.data.data.length > 0) {
            setSelected(r.data.data[0].id);
          }
        } else {
          setAcErr(r.data.message);
        }
      })
      .catch(err => setAcErr(err.response?.data?.message || err.message))
      .finally(() => setAcL(false));
    /* eslint-disable-next-line */
  }, []);

  // Load statement whenever account/period changes
  const loadStatement = () => {
    if (!selectedId || !start || !end) return;
    setLoading(true); setError(''); setData(null);
    axios.get(reportBankStatementURL, {
      headers: headers(),
      params: { account_id: selectedId, start, end },
    })
      .then(r => { if (r.data.success) setData(r.data); else setError(r.data.message); })
      .catch(err => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadStatement(); /* eslint-disable-next-line */ }, [selectedId, start, end]);

  const selectedAccount = useMemo(
    () => accounts.find(a => a.id === selectedId) || null,
    [accounts, selectedId]
  );

  // ─── PRINT ────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    if (!printRef.current) return;
    const w = window.open('', '_blank', 'width=1200,height=820');
    if (!w) return;
    w.document.write(`
      <html><head><title>Bank Statement - ${data?.account.name || ''}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: 'Helvetica Neue', -apple-system, Segoe UI, Roboto, Arial, sans-serif; margin: 28px 32px; color: #111; }
        .letterhead { display: flex; align-items: center; gap: 20px; padding-bottom: 14px; border-bottom: 3px double ${NAVY}; }
        .letterhead img { width: 84px; height: 84px; object-fit: contain; border-radius: 6px; }
        .letterhead .info h1 { margin: 0; font-size: 26px; letter-spacing: 0.02em; color: ${NAVY}; }
        .letterhead .info p { margin: 3px 0 0; color: #555; font-size: 12px; }
        .stmt-title { text-align: center; margin: 22px 0 4px; }
        .stmt-title .name { font-size: 15px; font-weight: 800; letter-spacing: 0.16em; color: ${NAVY}; border-top: 1.5px solid ${NAVY}; border-bottom: 1.5px solid ${NAVY}; display: inline-block; padding: 4px 24px; }
        .stmt-title .period { margin-top: 8px; font-size: 12px; color: #555; }
        .acct-card { margin-top: 16px; padding: 12px 16px; background: #f7f9fc; border-left: 4px solid ${NAVY}; border-radius: 4px; display: flex; justify-content: space-between; align-items: center; }
        .acct-card .name { font-size: 15px; font-weight: 800; color: ${NAVY}; letter-spacing: 0.05em; text-transform: uppercase; }
        .acct-card .meta { font-size: 11px; color: #555; }
        table { width: 100%; border-collapse: collapse; margin-top: 14px; }
        thead th { text-align: left; font-size: 10.5px; letter-spacing: 0.06em; text-transform: uppercase; color: #fff; background: ${NAVY}; padding: 8px 10px; }
        thead th.right { text-align: right; }
        tbody td { padding: 6px 10px; font-size: 12px; border-bottom: 1px solid #eef0f4; }
        tbody tr:nth-child(even) td { background: #fafbfc; }
        tbody td.right { text-align: right; font-variant-numeric: tabular-nums; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
        .bal-row td { background: ${NAVY}; color: #fff; font-weight: 800; padding: 8px 10px; letter-spacing: 0.06em; text-transform: uppercase; font-size: 12px; }
        .bal-row .right { text-align: right; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
        .totals-row td { background: #eef2f7; color: ${NAVY}; font-weight: 800; padding: 8px 10px; text-transform: uppercase; font-size: 12px; border-top: 2px solid ${NAVY}; border-bottom: 2px solid ${NAVY}; }
        .close-row td { background: #FFF5DA; color: #6B4A08; font-weight: 800; padding: 12px 10px; text-transform: uppercase; font-size: 13px; letter-spacing: 0.08em; border-top: 3px double ${GOLD}; border-bottom: 3px double ${GOLD}; }
        .footer { display: flex; justify-content: space-between; margin-top: 30px; font-size: 10px; color: #888; }
        @page { size: A4 landscape; margin: 12mm; }
      </style>
      </head><body>${printRef.current.innerHTML}</body></html>
    `);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 300);
  };

  // ─── EXPORT PDF ───────────────────────────────────────────────────────────
  const handlePdf = () => {
    if (!data) return;
    const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 36;

    let y = 32;
    const logoSize = 52;
    let textLeft = margin;
    if (data.church?.logo && String(data.church.logo).startsWith('data:image')) {
      try {
        const fmtType = data.church.logo.substring(11, data.church.logo.indexOf(';'));
        doc.addImage(data.church.logo, fmtType.toUpperCase(), margin, y, logoSize, logoSize);
        textLeft = margin + logoSize + 12;
      } catch (e) { /* skip */ }
    }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(11, 37, 69);
    doc.text(data.church?.name || 'Church', textLeft, y + 18);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(90);
    const subInfo = [data.church?.address, data.church?.phone, data.church?.email].filter(Boolean).join('  ·  ');
    if (subInfo) doc.text(subInfo, textLeft, y + 32);
    y += Math.max(logoSize, 36) + 6;
    doc.setDrawColor(11, 37, 69); doc.setLineWidth(1.0);
    doc.line(margin, y, pageW - margin, y);
    doc.setLineWidth(0.4); doc.line(margin, y + 3, pageW - margin, y + 3);
    y += 16;

    // Title
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(11, 37, 69);
    const title = 'BANK STATEMENT';
    const titleW = doc.getTextWidth(title);
    const tx = (pageW - titleW) / 2;
    doc.setLineWidth(0.6);
    doc.line(tx - 16, y - 4, tx + titleW + 16, y - 4);
    doc.text(title, pageW / 2, y + 10, { align: 'center' });
    doc.line(tx - 16, y + 15, tx + titleW + 15, y + 15);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(90);
    doc.text(
      `For the period ${fmtDateLong(data.period.start_date)} to ${fmtDateLong(data.period.end_date)}`,
      pageW / 2, y + 30, { align: 'center' }
    );
    y += 46;

    // Account card
    doc.setDrawColor(11, 37, 69); doc.setFillColor(247, 249, 252);
    doc.setLineWidth(0);
    doc.rect(margin, y, pageW - margin * 2, 40, 'F');
    doc.setDrawColor(11, 37, 69); doc.setLineWidth(3);
    doc.line(margin, y, margin, y + 40);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(11, 37, 69);
    doc.text(data.account.name.toUpperCase(), margin + 12, y + 18);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(85);
    const openInfo = data.account.opening_date
      ? `Opened ${fmtDateShort(data.account.opening_date)}  ·  Opening balance GH₵ ${fmt(data.account.opening_balance)}`
      : `Opening balance GH₵ ${fmt(data.account.opening_balance)}`;
    doc.text(openInfo, margin + 12, y + 32);
    y += 52;

    // Body: b/fwd, transactions, totals, c/fwd
    const body = [];
    body.push([
      { content: `Balance Brought Forward — as at ${fmtDateShort(data.period.start_date)}`, colSpan: 5,
        styles: { fillColor: [11, 37, 69], textColor: 255, fontStyle: 'bold' } },
      { content: fmt(data.bfwd),
        styles: { fillColor: [11, 37, 69], textColor: 255, fontStyle: 'bold', halign: 'right' } },
    ]);

    data.transactions.forEach((t, i) => {
      body.push([
        String(i + 1),
        fmtDateShort(t.date),
        t.ref + (t.cheque_no ? ` (Chq ${t.cheque_no})` : ''),
        t.description,
        {
          content: t.deposit ? fmt(t.deposit) : '—',
          styles: { halign: 'right', textColor: t.deposit ? [15, 118, 110] : [148, 163, 184] },
        },
        {
          content: t.withdrawal ? fmt(t.withdrawal) : '—',
          styles: { halign: 'right', textColor: t.withdrawal ? [185, 28, 28] : [148, 163, 184] },
        },
        { content: fmt(t.running_balance),
          styles: { halign: 'right', fontStyle: 'bold' } },
      ]);
    });

    // Totals
    body.push([
      { content: `TOTALS — ${data.totals.tx_count} transaction${data.totals.tx_count === 1 ? '' : 's'}`,
        colSpan: 4,
        styles: { fontStyle: 'bold', fillColor: [238, 242, 247], textColor: [11, 37, 69] } },
      { content: fmt(data.totals.deposits),
        styles: { halign: 'right', fontStyle: 'bold', fillColor: [238, 242, 247], textColor: [15, 118, 110] } },
      { content: fmt(data.totals.withdrawals),
        styles: { halign: 'right', fontStyle: 'bold', fillColor: [238, 242, 247], textColor: [185, 28, 28] } },
      { content: '',
        styles: { fillColor: [238, 242, 247] } },
    ]);

    // Closing balance
    body.push([
      { content: `Balance Carried Forward — as at ${fmtDateShort(data.period.end_date)}`, colSpan: 5,
        styles: { fontStyle: 'bold', fillColor: [255, 245, 218], textColor: [107, 74, 8], fontSize: 12 } },
      { content: fmt(data.cfwd),
        styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 245, 218], textColor: [107, 74, 8], fontSize: 12 } },
    ]);

    autoTable(doc, {
      startY: y,
      head: [[
        '#',
        'Date',
        'Ref / Cheque',
        'Description',
        { content: 'Deposit (GH₵)',    styles: { halign: 'right' } },
        { content: 'Withdrawal (GH₵)', styles: { halign: 'right' } },
        { content: 'Balance (GH₵)',    styles: { halign: 'right' } },
      ]],
      body: body.map(row => {
        // Handle the balance-brought-forward row that spans 6 columns instead of 7
        if (row.length === 2) {
          // { colSpan: 5 } + amount = actually spans 6 cols; we need first col too
          return [{ content: '', styles: { fillColor: [11, 37, 69] } }, ...row];
        }
        return row;
      }),
      theme: 'grid',
      styles: { fontSize: 8.5, cellPadding: 4, lineColor: [230, 232, 238], lineWidth: 0.4 },
      headStyles: { fillColor: [11, 37, 69], textColor: 255, fontStyle: 'bold', halign: 'left', valign: 'middle' },
      alternateRowStyles: { fillColor: [250, 251, 252] },
      columnStyles: {
        0: { cellWidth: 26, halign: 'center' },
        1: { cellWidth: 66 },
        2: { cellWidth: 90 },
        3: { cellWidth: 'auto' },
        4: { cellWidth: 82, halign: 'right' },
        5: { cellWidth: 90, halign: 'right' },
        6: { cellWidth: 82, halign: 'right' },
      },
      margin: { top: 40, left: margin, right: margin, bottom: 40 },
      didDrawPage: () => {
        doc.setFontSize(8); doc.setTextColor(120);
        doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageW - margin, pageH - 18, { align: 'right' });
        doc.text(`Generated ${new Date().toLocaleString('en-GH')}`, margin, pageH - 18);
      },
    });

    const stamp = `${data.period.start_date}_to_${data.period.end_date}`;
    const nameStamp = data.account.name.replace(/[^\w]+/g, '_');
    doc.save(`Bank_Statement_${nameStamp}_${stamp.replace(/[\/\s]/g, '_')}.pdf`);
  };

  // ─── EXPORT EXCEL ─────────────────────────────────────────────────────────
  const handleExcel = () => {
    if (!data) return;
    const aoa = [];
    aoa.push([data.church?.name || 'Church']);
    if (data.church?.address) aoa.push([data.church.address]);
    const contact = [data.church?.phone, data.church?.email].filter(Boolean).join(' · ');
    if (contact) aoa.push([contact]);
    aoa.push([]);
    aoa.push(['BANK STATEMENT']);
    aoa.push([data.account.name]);
    aoa.push([`For the period ${fmtDateLong(data.period.start_date)} to ${fmtDateLong(data.period.end_date)}`]);
    aoa.push([]);
    aoa.push(['#', 'Date', 'Ref', 'Cheque No.', 'Description', 'Deposit (GH₵)', 'Withdrawal (GH₵)', 'Balance (GH₵)']);
    aoa.push(['', '', '', '', `Balance Brought Forward — ${fmtDateShort(data.period.start_date)}`, '', '', Number(data.bfwd)]);
    data.transactions.forEach((t, i) => {
      aoa.push([
        i + 1,
        t.date || '',
        t.ref || '',
        t.cheque_no || '',
        t.description || '',
        t.deposit    ? Number(t.deposit)    : '',
        t.withdrawal ? Number(t.withdrawal) : '',
        Number(t.running_balance),
      ]);
    });
    aoa.push(['', '', '', '', `TOTALS (${data.totals.tx_count} transactions)`, Number(data.totals.deposits), Number(data.totals.withdrawals), '']);
    aoa.push(['', '', '', '', `Balance Carried Forward — ${fmtDateShort(data.period.end_date)}`, '', '', Number(data.cfwd)]);

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [
      { wch: 5 }, { wch: 12 }, { wch: 18 }, { wch: 12 }, { wch: 44 },
      { wch: 16 }, { wch: 18 }, { wch: 16 },
    ];
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = 5; C <= 7; C++) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = ws[addr];
        if (cell && typeof cell.v === 'number') cell.z = '#,##0.00';
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Bank Statement');
    const stamp = `${data.period.start_date}_to_${data.period.end_date}`;
    const nameStamp = data.account.name.replace(/[^\w]+/g, '_');
    XLSX.writeFile(wb, `Bank_Statement_${nameStamp}_${stamp.replace(/[\/\s]/g, '_')}.xlsx`);
  };

  return (
    <Box>
      {/* Action bar */}
      <Stack direction="row" spacing={1} justifyContent="flex-end" mb={2} flexWrap="wrap">
        <Button size="small" variant="outlined" startIcon={<Refresh />}
          onClick={loadStatement} disabled={!selectedId}>Refresh</Button>
        <Button size="small" variant="outlined" startIcon={<Print />}
          onClick={handlePrint} disabled={!data}>Print</Button>
        <Button size="small" variant="outlined" startIcon={<GridOn />}
          onClick={handleExcel} disabled={!data}
          sx={{ color: '#0F6C3D', borderColor: '#0F6C3D', '&:hover': { borderColor: '#0F6C3D', bgcolor: '#0F6C3D0d' } }}>
          Export Excel
        </Button>
        <Button size="small" variant="contained" startIcon={<PictureAsPdf />}
          onClick={handlePdf} disabled={!data}
          sx={{ bgcolor: RED, '&:hover': { bgcolor: '#991B1B' } }}>
          Export PDF
        </Button>
      </Stack>

      <Grid container spacing={2}>
        {/* Left column: Bank accounts */}
        <Grid item xs={12} md={4} lg={3}>
          <Paper sx={{
            border: '1px solid #E2E8F0', borderRadius: 2,
            position: { md: 'sticky' }, top: { md: 16 },
            maxHeight: { md: 'calc(100vh - 200px)' },
            overflow: 'auto',
          }} elevation={0}>
            <Box sx={{
              px: 2, py: 1.5, bgcolor: NAVY, color: '#fff',
              display: 'flex', alignItems: 'center', gap: 1,
              borderRadius: '8px 8px 0 0',
            }}>
              <AccountBalance sx={{ fontSize: 18 }} />
              <Typography variant="subtitle2" fontWeight={800} sx={{ letterSpacing: '0.06em' }}>
                BANK ACCOUNTS
              </Typography>
            </Box>

            {accountsLoading ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress size={22} />
              </Box>
            ) : accountsErr ? (
              <Alert severity="error" sx={{ m: 1 }}>{accountsErr}</Alert>
            ) : accounts.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="caption">No bank accounts set up.</Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {accounts.map(a => {
                  const isSel = a.id === selectedId;
                  return (
                    <ListItemButton
                      key={a.id}
                      selected={isSel}
                      onClick={() => setSelected(a.id)}
                      sx={{
                        borderBottom: '1px solid #eef0f4',
                        '&.Mui-selected': { bgcolor: `${NAVY}15`, borderLeft: `4px solid ${NAVY}` },
                        '&.Mui-selected:hover': { bgcolor: `${NAVY}22` },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight={800} sx={{
                              color: NAVY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              {a.name}
                            </Typography>
                            {a.status !== 'active' && (
                              <Chip label={a.status} size="small"
                                sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#eee', color: '#666' }} />
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" sx={{ color: '#64748b', fontFamily: 'monospace', fontSize: 11 }}>
                            Opening GH₵ {fmt(a.opening_balance)}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Right column: Statement paper */}
        <Grid item xs={12} md={8} lg={9}>
          <Paper
            elevation={2}
            sx={{
              border: '1px solid #E2E8F0', borderRadius: 2,
              p: { xs: 2, md: 4 }, bgcolor: '#fff',
              boxShadow: '0 10px 30px rgba(15,23,42,0.06)',
              minHeight: 400,
            }}
          >
            {!selectedAccount ? (
              <Box sx={{ py: 10, textAlign: 'center', color: 'text.secondary' }}>
                <AccountBalance sx={{ fontSize: 48, color: '#c9d3e0', mb: 1 }} />
                <Typography variant="subtitle1" fontWeight={700}>Select a bank account</Typography>
                <Typography variant="body2">Pick an account from the left to view its statement.</Typography>
              </Box>
            ) : loading ? (
              <Box sx={{ py: 8, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Loading statement for {selectedAccount.name}…
                </Typography>
              </Box>
            ) : error ? (
              <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
            ) : !data ? null : (
              <Box ref={printRef}>
                {/* Letterhead */}
                <Box className="letterhead" sx={{
                  display: 'flex', alignItems: 'center', gap: 3,
                  pb: 2, borderBottom: `3px double ${RULE}`,
                }}>
                  {data.church?.logo ? (
                    <Box component="img" src={data.church.logo} alt="Logo"
                      sx={{ width: 84, height: 84, objectFit: 'contain', borderRadius: 1 }} />
                  ) : (
                    <Box sx={{
                      width: 84, height: 84, borderRadius: 1,
                      bgcolor: `${NAVY}12`, color: NAVY,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 800, fontSize: 28,
                    }}>
                      {(data.church?.name || 'C').charAt(0).toUpperCase()}
                    </Box>
                  )}
                  <Box className="info" sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={800}
                      sx={{ color: NAVY, letterSpacing: '0.02em', lineHeight: 1.1 }}>
                      {data.church?.name || 'Church'}
                    </Typography>
                    {(data.church?.address || data.church?.phone || data.church?.email) && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {[data.church?.address, data.church?.phone, data.church?.email].filter(Boolean).join('  ·  ')}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Title */}
                <Box className="stmt-title" sx={{ textAlign: 'center', mt: 3, mb: 1 }}>
                  <Typography className="name" sx={{
                    fontSize: 15, fontWeight: 800, letterSpacing: '0.16em', color: NAVY,
                    borderTop: `1.5px solid ${NAVY}`, borderBottom: `1.5px solid ${NAVY}`,
                    display: 'inline-block', px: 3, py: 0.5,
                  }}>
                    BANK STATEMENT
                  </Typography>
                  <Typography className="period" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    For the period <b>{fmtDateLong(data.period.start_date)}</b> to <b>{fmtDateLong(data.period.end_date)}</b>
                  </Typography>
                </Box>

                {/* Account card */}
                <Box className="acct-card" sx={{
                  mt: 2, p: 1.5, pl: 2,
                  bgcolor: '#f7f9fc', borderLeft: `4px solid ${NAVY}`,
                  borderRadius: 1,
                  display: 'flex', flexWrap: 'wrap', gap: 2,
                  justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <Box>
                    <Typography className="name" sx={{
                      fontSize: 15, fontWeight: 800, color: NAVY,
                      letterSpacing: '0.04em', textTransform: 'uppercase',
                    }}>
                      {data.account.name}
                    </Typography>
                    <Typography className="meta" variant="caption" sx={{ color: '#555' }}>
                      {data.account.opening_date
                        ? <>Opened <b>{fmtDateShort(data.account.opening_date)}</b> · </>
                        : null}
                      Opening balance <b>GH₵ {fmt(data.account.opening_balance)}</b>
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Chip size="small" label={`${data.totals.tx_count} transaction${data.totals.tx_count === 1 ? '' : 's'}`}
                      sx={{ bgcolor: `${NAVY}15`, color: NAVY, fontWeight: 700 }} />
                  </Stack>
                </Box>

                {/* Statement table */}
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 14 }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, width: 36 }}>#</th>
                      <th style={thStyle}>Date</th>
                      <th style={thStyle}>Ref / Cheque</th>
                      <th style={thStyle}>Description</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Deposit (GH₵)</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Withdrawal (GH₵)</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Balance (GH₵)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Balance b/fwd */}
                    <tr>
                      <td colSpan={6} style={balHeadLabelStyle}>
                        Balance Brought Forward — as at {fmtDateShort(data.period.start_date)}
                      </td>
                      <td style={balHeadAmountStyle}>{fmt(data.bfwd)}</td>
                    </tr>

                    {data.transactions.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{
                          padding: '24px 12px', textAlign: 'center',
                          color: '#94a3b8', fontStyle: 'italic', borderBottom: '1px solid #eef0f4',
                        }}>
                          No transactions in this period.
                        </td>
                      </tr>
                    ) : data.transactions.map((t, i) => (
                      <tr key={t.id} style={{ background: i % 2 === 1 ? '#fafbfc' : 'transparent' }}>
                        <td style={{ ...cellStyle, textAlign: 'center', color: '#94a3b8' }}>{i + 1}</td>
                        <td style={cellStyle}>{fmtDateShort(t.date)}</td>
                        <td style={cellStyle}>
                          <Typography component="span" sx={{ fontWeight: 700, color: NAVY, fontSize: 12.5 }}>
                            {t.ref}
                          </Typography>
                          {t.cheque_no && (
                            <Typography component="span" sx={{ ml: 0.5, fontSize: 10.5, color: '#64748b' }}>
                              Chq {t.cheque_no}
                            </Typography>
                          )}
                          {t.is_bank_to_bank && (
                            <span style={{
                              display: 'inline-block', marginLeft: 6, padding: '1px 6px',
                              borderRadius: 8, background: '#eef2f7', color: NAVY,
                              fontSize: 10, fontWeight: 700,
                            }}>B→B</span>
                          )}
                        </td>
                        <td style={{ ...cellStyle, color: '#475569', maxWidth: 280 }}>
                          {t.description}
                          {t.payee && (
                            <span style={{ display: 'block', fontSize: 10.5, color: '#94a3b8', marginTop: 2 }}>
                              Payee: {t.payee}
                            </span>
                          )}
                        </td>
                        <td style={{ ...amountStyle, color: t.deposit ? GREEN : '#cbd5e1', fontWeight: t.deposit ? 700 : 400 }}>
                          {t.deposit ? fmt(t.deposit) : '—'}
                        </td>
                        <td style={{ ...amountStyle, color: t.withdrawal ? RED : '#cbd5e1', fontWeight: t.withdrawal ? 700 : 400 }}>
                          {t.withdrawal ? fmt(t.withdrawal) : '—'}
                        </td>
                        <td style={{ ...amountStyle, fontWeight: 800, color: NAVY }}>
                          {fmtSigned(t.running_balance)}
                        </td>
                      </tr>
                    ))}

                    {/* Totals row */}
                    <tr>
                      <td colSpan={4} style={totalsLabelStyle}>
                        TOTALS — {data.totals.tx_count} transaction{data.totals.tx_count === 1 ? '' : 's'}
                        {' '}({data.totals.deposit_count} deposit{data.totals.deposit_count === 1 ? '' : 's'},
                        {' '}{data.totals.withdrawal_count} payment{data.totals.withdrawal_count === 1 ? '' : 's'})
                      </td>
                      <td style={{ ...totalsAmountStyle, color: GREEN }}>{fmt(data.totals.deposits)}</td>
                      <td style={{ ...totalsAmountStyle, color: RED }}>{fmt(data.totals.withdrawals)}</td>
                      <td style={totalsAmountStyle}></td>
                    </tr>

                    {/* Balance c/fwd */}
                    <tr>
                      <td colSpan={6} style={closeLabelStyle}>
                        Balance Carried Forward — as at {fmtDateShort(data.period.end_date)}
                      </td>
                      <td style={closeAmountStyle}>{fmt(data.cfwd)}</td>
                    </tr>
                  </tbody>
                </table>

                {/* Footer */}
                <Box className="footer" sx={{
                  display: 'flex', justifyContent: 'space-between', mt: 4,
                  fontSize: 10, color: '#888',
                }}>
                  <span>Generated {new Date().toLocaleString('en-GH')}</span>
                  <span>{data.church?.name || ''}</span>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

// ─── Inline styles ─────────────────────────────────────────────────────────
const thStyle = {
  textAlign: 'left', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase',
  color: '#fff', background: NAVY, padding: '9px 10px', fontWeight: 700,
};
const cellStyle    = { padding: '6px 10px', fontSize: 12.5, borderBottom: '1px solid #eef0f4', color: '#1e293b' };
const amountStyle  = { padding: '6px 10px', fontSize: 13, borderBottom: '1px solid #eef0f4',
                       textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                       fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' };
const balHeadLabelStyle = {
  background: NAVY, color: '#fff', fontWeight: 800,
  padding: '10px 10px', letterSpacing: '0.06em', textTransform: 'uppercase',
  fontSize: 12,
};
const balHeadAmountStyle = {
  background: NAVY, color: '#fff', fontWeight: 800,
  padding: '10px 10px', textAlign: 'right',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};
const totalsLabelStyle = {
  background: '#eef2f7', color: NAVY, fontWeight: 800,
  padding: '10px 10px', textTransform: 'uppercase', fontSize: 12,
  borderTop: `2px solid ${NAVY}`, borderBottom: `2px solid ${NAVY}`,
};
const totalsAmountStyle = {
  background: '#eef2f7', fontWeight: 800,
  padding: '10px 10px', textAlign: 'right',
  borderTop: `2px solid ${NAVY}`, borderBottom: `2px solid ${NAVY}`,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};
const closeLabelStyle = {
  background: '#FFF5DA', color: '#6B4A08', fontWeight: 800,
  padding: '12px 10px', letterSpacing: '0.08em', textTransform: 'uppercase',
  fontSize: 13, borderTop: `3px double ${GOLD}`, borderBottom: `3px double ${GOLD}`,
};
const closeAmountStyle = {
  background: '#FFF5DA', color: '#6B4A08', fontWeight: 800,
  padding: '12px 10px', textAlign: 'right',
  borderTop: `3px double ${GOLD}`, borderBottom: `3px double ${GOLD}`,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 14,
};
